import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import slugify from "slugify";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Pagination,
  CircularProgress,
  Autocomplete,
  TextField
} from "@mui/material";
import { capitalize, formatSalaryInternal, getFormattedDate } from "bundles/common/utils/helpers";
import { statusChipColor } from "bundles/common/utils/constants";
import { archiveOffer, deleteOffer } from "bundles/common/utils/actions/job_offers";
import { duplicateOffer } from "bundles/common/utils/actions/job_offers";
import ConfirmModal from "bundles/Internal/components/ConfirmModal";
import NoData from "internal/no-data.svg";
import sh from "bundles/common/utils/sh";
import InfoIcon from "@mui/icons-material/Info";
import CustomExpansionControl from "bundles/Internal/components/partials/CustomExpansionControl";
import ExpandedJobOffer from "bundles/Internal/components/partials/ExpandedJobOffer";
import CloseIcon from "@mui/icons-material/Close";

const Dashboard = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentUser = useSelector((state) => state.current_user);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [selectedJobInfo, setSelectedJobInfo] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const customStyles = {
    headCells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(2)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    },
    cells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(2)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    },
    responsiveWrapper: {
      style: {
        overflowX: "hidden"
      }
    }
  };

  const columns = [
    {
      name: "",
      button: true,
      cell: (row) => (
        <CustomExpansionControl
          row={row}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          applicationsCount={row.job_applications.length}
          isRowExpandable={() => isRowExpandable(row)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "56px",
    },
    {
      name: "Job title",
      selector: row => row.job_title,
      width: "20%"
    },
    {
      name: "City",
      selector: row => row.city,
      width: "10%"
    },
    {
      name: "Company",
      selector: row => row.company?.name || "N/A",
      width: "12%",
    },
    {
      name: "Status",
      selector: row => {
        let value = capitalize(row.offer_status);
        return <Chip color={statusChipColor[row.offer_status] || "default"} label={value}/>;
      },
      center: true,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        if (rowA.offer_status < rowB.offer_status) return -1;
        if (rowA.offer_status > rowB.offer_status) return 1;

        const dateA = new Date(rowA.published_at);
        const dateB = new Date(rowB.published_at);
        return dateA - dateB;
      },
      width: "10%"
    },
    {
      name: "Publication date",
      selector: row => {
        const dateToShow = row.offer_status === "scheduled" ? row.scheduled_at : row.published_at;
        const formattedDate = getFormattedDate(dateToShow, false);
        return (row.offer_status === "scheduled" || row.offer_status === "archived") ?
          <span style={{ color: "grey" }}>{formattedDate}</span> :
          formattedDate || "/";
      },
      sortable: true,
      width: "10%"
    },
    {
      name: "More Info",
      cell: (row) => (
        <IconButton
          onClick={() => {
            setSelectedJobInfo(row);
            setIsInfoModalOpen(true);
          }}
          size="small"
        >
          <InfoIcon />
        </IconButton>
      ),
      width: "8%",
    },
    {
      name: "Actions",
      selector: row => {
        return (<Box>
          {row.offer_status !== "archived" &&
            <Button
              size="small"
              variant="text"
              onClick={() => navigate(`edit_ad/${row.id}`)}
            >
              Edit
            </Button>
          }
          {(row.offer_status === "draft" || row.offer_status === "scheduled") &&
            <Button
              size="small"
              variant="text"
              disabled={loading}
              onClick={() => onDeleteClicked(row.id)}
            >
              Delete
            </Button>
          }
          {row.offer_status == "online" &&
            <Button
              size="small"
              variant="text"
              disabled={loading}
              onClick={() => onArchiveClicked(row.id)}
            >
              Archive
            </Button>
          }
          {row.offer_status == "archived" &&
            <Button
              size="small"
              variant="text"
              disabled={loading}
              onClick={() => duplicateOffer(row.id, setLoading, authenticityToken, onDuplicateSuccessful, onUpdateFail)}
            >
              Duplicate
            </Button>
          }
        </Box>);
      },
      width: "12%"
    },
    {
      name: "View Job",
      selector: row => {
        if (row.offer_status !== "online") {
          return "/";
        }

        const slugifyOptions = {
          lower: true,
          strict: true,
          remove: /[*+~.()'"!:@]/g
        };
        const jobTitleSlug = slugify(row.job_title, slugifyOptions);
        const publicURL = window.location.origin;
        const jobUrl = `${publicURL}/${row.offer_language}/jobs/${jobTitleSlug}/${row.id}`;

        return (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            justifyContent: "center"
          }}>
            <Button size="small" onClick={() => window.open(jobUrl, "_blank")}>
              <img
                src="/sander-logo.svg"
                alt="Sander"
                style={{
                  width: row.stepstone_url ? "60px" : "68px",
                  height: row.stepstone_url ? "60px" : "68px",
                  objectFit: "contain"
                }}
              />
            </Button>
            {row.stepstone_url && (
              <Button
                size="small"
                onClick={() => window.open(row.stepstone_url, "_blank")}
              >
                <img
                  src="/static/images/logos/StepStone.svg"
                  alt="Stepstone"
                  style={{ width: "60px", height: "60px", objectFit: "contain" }}
                />
              </Button>
            )}
          </div>
        );
      },
      center: true,
      width: "16%"
    }
  ];

  const fetchJobs = ({
    page = currentPage,
    status = selectedStatus,
    companyId = selectedCompany?.id
  } = {}) => {
    setLoading(true);

    const params = {
      authenticity_token: authenticityToken,
      selected_employee: currentUser.id,
      page,
      offer_status: status,
      company_id: companyId,
      order_by: "created_at"
    };

    sh.get("internal/job_offers", { params })
      .then((res) => {
        setJobs(res?.data?.data);
        setPageCount(res.data?.pagy?.pages);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
        setPageLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setPageLoading(true);
    fetchJobs({
      page: value,
      status: selectedStatus
    });
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    setPageLoading(true);
    fetchJobs({
      page: 1,
      status
    });
  };

  const onDeleteClicked = (offerId) => {
    setSelectedOfferId(offerId);
    setIsDeleteConfirmModalOpen(true);
  };

  const onDeleteConfirmed = () => {
    deleteOffer(selectedOfferId, setLoading, authenticityToken, onDeleteSuccessful, onUpdateFail);
  };

  const onArchiveClicked = (offerId) => {
    setSelectedOfferId(offerId);
    setIsArchiveConfirmModalOpen(true);
  };

  const onArchiveConfirmed = () => {
    archiveOffer(selectedOfferId, setLoading, authenticityToken, onArchiveSuccessful, onUpdateFail);
  };

  const onConfirmModalCloses = () => {
    setSelectedOfferId(null);
  };

  const onDuplicateSuccessful = (newJob) => {
    if (selectedStatus === "all") {
      setJobs(prevJobs => [newJob, ...prevJobs]);
    }
    enqueueSnackbar("Offer successfully duplicated!", {
      variant: "success",
    });
  };

  const onArchiveSuccessful = () => {
    fetchJobs();
    fetchDashboardStats();
    enqueueSnackbar("Offer successfully archived!", {
      variant: "success",
    });
  };

  const onDeleteSuccessful = () => {
    fetchJobs();
    enqueueSnackbar("Offer successfully deleted!", {
      variant: "success",
    });
  };

  const onUpdateFail = () => {
    enqueueSnackbar(
      "Oops, an error occured.",
      { variant: "error" }
    );
  };

  const isRowExpandable = (row) => {
    return ["online", "archived"].includes(row.offer_status) && row.job_applications && row.job_applications.length > 0;
  };

  const fetchDashboardStats = () => {
    sh.get("/internal/dashboard_stats", {
      authenticity_token: authenticityToken
    })
      .then((res) => {
        if (res?.data) {
          setDashboardStats(res.data);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch dashboard stats:", error);
      });
  };

  const handleFlagUpdate = (applicationId, newFlag) => {
    sh.put(`/internal/job_applications/${applicationId}/update_flag`, {
      authenticity_token: authenticityToken,
      flag: newFlag
    })
      .then(() => {
        setJobs(prevJobs =>
          prevJobs.map(job => ({
            ...job,
            job_applications: job.job_applications?.map(app =>
              app.id === applicationId ? { ...app, flag: newFlag } : app
            )
          }))
        );
      })
      .catch(() => {
        enqueueSnackbar("Failed to update flag", { variant: "error" });
      });
  };

  const handleCompanyChange = (event, newValue) => {
    setSelectedCompany(newValue);
    setCurrentPage(1);
    setPageLoading(true);
    fetchJobs({
      page: 1,
      companyId: newValue?.id || null
    });
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchJobs();
  }, []);

  return (
    <>
      <Container maxWidth="100%">
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "24px", marginTop: "20px"}}>
          <h1 style={{fontSize: "24px"}}>Welcome to your dashboard, {currentUser.first_name}!</h1>
          <Button variant="squared" color="primaryContained" href="internal/new_ad">New job offer</Button>
        </Box>
        <Box>
          {dashboardStats && (
            <Box style={{ display: "flex", justifyContent: "space-around", margin: "28px" }}>
              <Box style={{ border: "1px solid #ccc", padding: "16px", borderRadius: "8px" }}>
                <Typography variant="h7">Online Job Offers</Typography>
                <Typography variant="h5" style={{ display: "flex", justifyContent: "center" }}>
                  {dashboardStats.online_job_offers_count}
                </Typography>
              </Box>
              <Box style={{ border: "1px solid #ccc", padding: "16px", borderRadius: "8px" }}>
                <Typography variant="h7">Published This Week</Typography>
                <Typography variant="h5" style={{ display: "flex", justifyContent: "center" }}>
                  {dashboardStats.created_this_week_count}
                </Typography>
              </Box>
              <Box style={{ border: "1px solid #ccc", padding: "16px", borderRadius: "8px" }}>
                <Typography variant="h7">Applications Received This Week</Typography>
                <Typography variant="h5" style={{ display: "flex", justifyContent: "center" }}>
                  {dashboardStats.job_applications_this_week_count}
                </Typography>
              </Box>
              <Box style={{ border: "1px solid #ccc", padding: "16px", borderRadius: "8px" }}>
                <Typography variant="h7">Planned Job Offers</Typography>
                <Typography variant="h5" style={{ display: "flex", justifyContent: "center" }}>
                  {dashboardStats.planned_job_offers}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {dashboardStats?.total_job_offers_count > 0 &&
          <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1} marginBottom={2}>
            <Box display="flex" gap={2}>
              <Button
                size="small"
                variant={selectedStatus === "all" ? "contained" : "outlined"}
                onClick={() => handleStatusChange("all")}
              >
                All
              </Button>
              <Button
                size="small"
                variant={selectedStatus === "online" ? "contained" : "outlined"}
                onClick={() => handleStatusChange("online")}
              >
                Online
              </Button>
              <Button
                size="small"
                variant={selectedStatus === "draft" ? "contained" : "outlined"}
                onClick={() => handleStatusChange("draft")}
              >
                Drafts
              </Button>
              <Button
                size="small"
                variant={selectedStatus === "scheduled" ? "contained" : "outlined"}
                onClick={() => handleStatusChange("scheduled")}
              >
                Scheduled
              </Button>
              <Button
                size="small"
                variant={selectedStatus === "archived" ? "contained" : "outlined"}
                onClick={() => handleStatusChange("archived")}
              >
                Archived
              </Button>
            </Box>
            <Autocomplete
              value={selectedCompany}
              onChange={handleCompanyChange}
              options={currentUser.job_offers_companies || []}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Filter by company"
                  sx={{ width: 230 }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        }
        <DataTable
          noDataComponent={
            <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", rowGap: "16px" }}>
              {selectedStatus === "all" ? (
                <h3>No jobs created yet</h3>
              ) : (
                <h3>No {selectedStatus} jobs</h3>
              )}
              <Button variant="squared" color="primaryContained" href="internal/new_ad">Click here to create one</Button>
              <img style={{ width: "45%", marginTop: "64px" }} src={NoData} />
            </Box>
          }
          columns={columns}
          data={jobs}
          defaultSortAsc={false}
          pointerOnHover
          responsive
          striped
          expandableRows
          expandableRowExpanded={row => expandedRows.includes(row.id)}
          expandableRowsComponent={({ data }) => {
            return (
              <ExpandedJobOffer
                data={data}
                readOnly={false}
                onFlagUpdate={handleFlagUpdate}
              />
            );
          }}
          expandableRowsHideExpander={true}
          progressPending={pageLoading}
          progressComponent={
            <Box sx={{ padding: "36px" }}>
              <CircularProgress />
            </Box>
          }
          customStyles={{
            ...customStyles,
            table: {
              style: {
                marginBottom: pageCount <= 1 ? "32px" : "0px"
              }
            }
          }}
        />
        {!loading && pageCount > 1 && (
          <Box display="flex" justifyContent="center" mt={4} mb={4}>
            <Pagination count={pageCount} page={currentPage} onChange={handlePageChange} />
          </Box>
        )}

        {/* Job Info Modal */}
        <Dialog
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              maxHeight: "80vh"
            }
          }}
        >
          {selectedJobInfo && (
            <>
              <DialogTitle sx={{
                bgcolor: "primary.main",
                color: "white",
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start"
              }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedJobInfo.job_title}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 0.5, opacity: 0.9 }}>
                    {selectedJobInfo.company?.name}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1
                }}>
                  <IconButton
                    onClick={() => setIsInfoModalOpen(false)}
                    sx={{
                      color: "white",
                      mt: -1,
                      mr: -1,
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.2)"
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  {["online", "draft", "archived"].includes(selectedJobInfo.offer_status) && (
                    <Chip
                      label={selectedJobInfo.offer_status === "online" ? "View Job" : capitalize(selectedJobInfo.offer_status)}
                      size="small"
                      onClick={() => {
                        if (selectedJobInfo.offer_status === "online") {
                          const slugifyOptions = { lower: true, strict: true, remove: /[*+~.()'"!:@]/g };
                          const jobTitleSlug = slugify(selectedJobInfo.job_title, slugifyOptions);
                          const jobUrl = `${window.location.origin}/jobs/${jobTitleSlug}/${selectedJobInfo.id}`;
                          window.open(jobUrl, "_blank");
                        }
                      }}
                      sx={{
                        bgcolor: "white",
                        color: "primary.main",
                        fontWeight: 500,
                        cursor: selectedJobInfo.offer_status === "online" ? "pointer" : "default",
                        "&:hover": {
                          bgcolor: selectedJobInfo.offer_status === "online" ? "grey.200" : "white"
                        }
                      }}
                    />
                  )}
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      borderRadius: "4px"
                    }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Location
                      </Typography>
                      <Typography variant="body2">
                        {selectedJobInfo.city || "Not specified"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      borderRadius: "4px"
                    }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Category
                      </Typography>
                      <Typography variant="body2">
                        {selectedJobInfo.category?.name || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      borderRadius: "4px"
                    }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Job Status
                      </Typography>
                      <Typography variant="body2">
                        {capitalize(selectedJobInfo.job_status)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      borderRadius: "4px"
                    }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Salary
                      </Typography>
                      <Typography variant="body2">
                        {selectedJobInfo.salary_type === "no-salary" ? (
                          "No salary"
                        ) : (selectedJobInfo.min_salary || selectedJobInfo.min_salary === 0) && selectedJobInfo.max_salary ? (
                          formatSalaryInternal(selectedJobInfo.salary_type, selectedJobInfo.min_salary, selectedJobInfo.max_salary)
                        ) : (
                          "Not specified"
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "primary.main",
                      bgcolor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      borderRadius: "4px",
                      "& p": { mb: 1 }
                    }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                        Summary
                      </Typography>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{ __html: selectedJobInfo.summary }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        setIsOpen={setIsDeleteConfirmModalOpen}
        handleConfirm={onDeleteConfirmed}
        label="You're about to delete this offer."
        onCloseCallback={onConfirmModalCloses}
      />
      <ConfirmModal
        isOpen={isArchiveConfirmModalOpen}
        setIsOpen={setIsArchiveConfirmModalOpen}
        handleConfirm={onArchiveConfirmed}
        label="You're about to archive this offer."
        onCloseCallback={onConfirmModalCloses}
      />
    </>
  );
};

export default Dashboard;
